import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { fmtEur } from "@/lib/format";

/** Tabella finanziaria generica: ricerca, ordinamento, paginazione, totali.
 * columns: [{key, label, render?, num?, sortValue?, total?}] */
export function DataTable({ columns, rows, testId = "table", pageSize = 25, searchable = true, onRowClick, emptyText = "Nessun dato", footer }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [page, setPage] = useState(0);

  const filtered = rows.filter((r) => !q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
  const sorted = sort.key
    ? [...filtered].sort((a, b) => {
        const col = columns.find((c) => c.key === sort.key);
        const va = col?.sortValue ? col.sortValue(a) : a[sort.key];
        const vb = col?.sortValue ? col.sortValue(b) : b[sort.key];
        if (va === vb) return 0;
        if (va === null || va === undefined) return 1;
        if (vb === null || vb === undefined) return -1;
        return (va > vb ? 1 : -1) * sort.dir;
      })
    : filtered;
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const view = sorted.slice(page * pageSize, (page + 1) * pageSize);
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));

  return (
    <div className="bg-white border rounded-md shadow-sm" data-testid={testId}>
      {searchable && (
        <div className="flex items-center gap-2 p-3 border-b">
          <Search className="w-4 h-4 text-slate-400" />
          <input data-testid={`${testId}-search`} value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Cerca…" className="text-sm outline-none flex-1 bg-transparent" />
          <span className="text-xs text-slate-500">{sorted.length} righe</span>
        </div>
      )}
      <div className="overflow-x-auto max-h-[70vh]">
        <table className="w-full fin-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={c.num ? "!text-right" : ""}>
                  <button type="button" onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-primary" data-testid={`${testId}-sort-${c.key}`}>
                    {c.label} <ArrowUpDown className="w-3 h-3 opacity-50" />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.length === 0 && (
              <tr><td colSpan={columns.length} className="text-center text-slate-500 py-8">{emptyText}</td></tr>
            )}
            {view.map((r, i) => (
              <tr key={r.id || i} data-testid={`${testId}-row`} onClick={onRowClick ? () => onRowClick(r) : undefined} className={onRowClick ? "cursor-pointer" : ""}>
                {columns.map((c) => (
                  <td key={c.key} className={c.num ? "num" : ""}>{c.render ? c.render(r) : r[c.key] ?? "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
          {(footer || columns.some((c) => c.total)) && (
            <tfoot>
              <tr>
                {columns.map((c, i) => (
                  <td key={c.key} className={c.num ? "num" : ""}>
                    {i === 0 && !c.total ? "Totale" : c.total ? fmtEur(sorted.reduce((s, r) => s + (Number(c.sortValue ? c.sortValue(r) : r[c.key]) || 0), 0)) : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 p-2 border-t text-sm">
          <button type="button" data-testid={`${testId}-prev`} disabled={page === 0} onClick={() => setPage(page - 1)} className="p-1 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span>Pagina {page + 1} di {pages}</span>
          <button type="button" data-testid={`${testId}-next`} disabled={page >= pages - 1} onClick={() => setPage(page + 1)} className="p-1 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

export const LinkCell = ({ to, children, testId }) => <Link to={to} data-testid={testId} className="text-primary hover:underline font-medium">{children}</Link>;
